import { InfoOutlined } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import clsx from "clsx";
import { FC, ReactNode } from "react";

import { FramerTooltip } from "@ryde/controls";
import { TOOLTIP_DELAY_BUTTONS, useIsMobile } from "@ryde/shared";

import { LiveMapPollInfoPillStyled } from "./live-map.styled";

export interface ILiveMapPollStatusPillProps {
  tooltip: ReactNode;
  isInFlight: boolean;
  isPollingActive: boolean;
  hasSyncedAtLeastOnce: boolean;
  children: ReactNode;
  /** Accessible label for the inline poll-info trigger (grid/map sync details). */
  pollInfoAriaLabel?: string;
  /** `inline` drops the gray pill — for chip rows that act as their own toggles. */
  variant?: "pill" | "inline";
}

export const LiveMapPollStatusPill: FC<ILiveMapPollStatusPillProps> = ({
  tooltip,
  isInFlight,
  isPollingActive,
  hasSyncedAtLeastOnce,
  children,
  pollInfoAriaLabel,
  variant = "pill",
}) => {
  const { isMobile } = useIsMobile();

  const networkDot = (
    <span
      aria-hidden
      className={clsx("live-map-poll-network-dot", {
        ["live-map-poll-network-dot--in-flight"]: isInFlight,
        ["live-map-poll-network-dot--sync-active"]:
          isPollingActive && !isInFlight,
        ["live-map-poll-network-dot--sync-paused"]:
          !isPollingActive && !isInFlight && hasSyncedAtLeastOnce,
      })}
    />
  );

  const pollInfoTrigger = (
    <FramerTooltip content={tooltip} mouseEnterDelay={TOOLTIP_DELAY_BUTTONS}>
      <IconButton
        size="small"
        color="info"
        className="live-map-poll-info-icon"
        aria-label={pollInfoAriaLabel}
      >
        <InfoOutlined fontSize="small" />
      </IconButton>
    </FramerTooltip>
  );

  if (variant === "inline") {
    return (
      <LiveMapPollInfoPillStyled className="live-map-poll-info--inline">
        {!isMobile && pollInfoTrigger}
        {networkDot}
        {children}
      </LiveMapPollInfoPillStyled>
    );
  }

  return (
    <FramerTooltip content={tooltip} mouseEnterDelay={TOOLTIP_DELAY_BUTTONS}>
      <LiveMapPollInfoPillStyled>
        {networkDot}
        {children}
      </LiveMapPollInfoPillStyled>
    </FramerTooltip>
  );
};
